import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Titled card with optional full-width footer action buttons (Figma User Details / InfoBox.kt).
 * Title sits above the card; rows are children (<InfoRow>).
 */
export interface InfoBoxAction {
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
}

export function InfoBox({
  title,
  actions,
  children,
}: {
  title: string;
  actions?: InfoBoxAction[];
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-base font-normal text-foreground">{title}</h2>
      <div className="overflow-hidden rounded-[10px] border bg-card">
        <div className="divide-y">{children}</div>
        {actions && actions.length > 0 ? (
          <div className="flex flex-col gap-2 border-t px-5 py-2.5">
            {actions.map((a) => (
              <Button
                key={a.label}
                size="sm"
                className="w-full"
                variant={a.variant ?? 'default'}
                onClick={a.onClick}
                disabled={a.disabled}
              >
                {a.label}
              </Button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/**
 * A single label/value row (port of InfoRow.kt). Action-style rows show a chevron;
 * clickable rows open via onClick. Long values get a more/less toggle.
 */
export function InfoRow({
  label,
  value,
  clickable,
  onClick,
  longText,
  chevron,
}: {
  label: string;
  value?: string | null | undefined;
  clickable?: boolean;
  onClick?: () => void;
  longText?: boolean;
  /** Show chevron affordance without making the row interactive (stubs). */
  chevron?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const display = value ?? '—';
  const hasValue = value != null && String(value).trim() !== '';
  const showChevron = Boolean(clickable || chevron);

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 px-5 py-2.5 text-base',
        clickable && 'cursor-pointer hover:bg-muted/50',
        chevron && !clickable && 'text-muted-foreground',
      )}
      onClick={clickable ? onClick : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <span className="shrink-0">{label}</span>
      <div className="flex min-w-0 items-center gap-1 text-right">
        {hasValue || longText ? (
          longText ? (
            <span className="text-left">
              <span className={cn(!expanded && 'line-clamp-2')}>{display}</span>
              {display.length > 80 && (
                <button
                  type="button"
                  className="ml-1 text-primary hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpanded((v) => !v);
                  }}
                >
                  {expanded ? 'less' : '… more'}
                </button>
              )}
            </span>
          ) : (
            <span className="truncate">{display}</span>
          )
        ) : null}
        {showChevron ? (
          <ChevronRight className="size-5 shrink-0 text-muted-foreground" />
        ) : null}
      </div>
    </div>
  );
}
